const DESKTOP_QUERY = '(min-width: 990px)';
const OPEN_DELAY = 70;
const CLOSE_DELAY = 320;
const PANEL_OVERLAP = 4;

class WelluviNavigation {
  constructor() {
    this.media = window.matchMedia(DESKTOP_QUERY);
    this.template = document.querySelector('#WelluviDesktopNavigationTemplate');
    this.headerMenu = document.querySelector('header-menu');
    this.headerComponent = document.querySelector('#header-component');
    this.openTimer = null;
    this.closeTimer = null;
    this.activeId = null;
    this.pointerInside = false;

    if (!(this.template instanceof HTMLTemplateElement) || !this.headerMenu || !this.headerComponent) return;

    this.mount();
    this.bind();
    this.syncMode();
  }

  mount() {
    const fragment = this.template.content.cloneNode(true);
    const root = fragment.querySelector('[data-welluvi-nav]');
    if (!root) return;

    /*
      Replace the complete Horizon desktop host, not its children.
      Horizon hydrates <header-menu> and restores its server-rendered children;
      replacing the host prevents that hydration pass from deleting our menu.
      The mobile drawer is a separate .header__drawer component and is untouched.
    */
    this.headerMenu.replaceWith(fragment);
    this.root = root;
    this.navBar = this.root.querySelector('.welluvi-nav__bar');

    /* Keep the first category row visually connected to the panel edge. */
    this.root.querySelectorAll('.welluvi-nav__columns').forEach((columns) => {
      columns.style.paddingTop = 'clamp(0.65rem, 0.9vw, 1rem)';
    });

    this.headerComponent.classList.add('welluvi-nav-ready');
    this.positionPanels();
  }

  bind() {
    if (!this.root) return;

    /*
      Any pointer movement inside the complete navigation tree, including a
      fixed-position panel, must cancel a pending close. This prevents the
      dropdown from disappearing while the cursor travels from a trigger into
      its panel.
    */
    this.root.addEventListener('pointerover', (event) => {
      this.pointerInside = true;
      this.cancelClose();

      const trigger = event.target.closest('[data-welluvi-trigger][aria-controls]');
      if (trigger) this.queueOpen(trigger.dataset.welluviTrigger);
    });

    this.root.addEventListener('pointerout', (event) => {
      const nextTarget = event.relatedTarget;
      if (nextTarget instanceof Node && this.root.contains(nextTarget)) return;

      this.pointerInside = false;
      this.queueClose();
    });

    this.root.querySelectorAll('[data-welluvi-panel]').forEach((panel) => {
      panel.addEventListener('pointerenter', () => {
        this.pointerInside = true;
        this.cancelClose();
      });

      panel.addEventListener('pointerleave', (event) => {
        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Node && this.root.contains(nextTarget)) return;

        this.pointerInside = false;
        this.queueClose();
      });
    });

    this.root.addEventListener('focusin', (event) => {
      this.cancelClose();
      const trigger = event.target.closest('[data-welluvi-trigger][aria-controls]');
      if (trigger) this.open(trigger.dataset.welluviTrigger);
    });

    this.root.addEventListener('focusout', (event) => {
      const nextTarget = event.relatedTarget;
      if (nextTarget instanceof Node && this.root.contains(nextTarget)) return;
      this.queueClose();
    });

    this.root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-welluvi-trigger][aria-controls]');
      if (!trigger) return;

      if (window.matchMedia('(hover: none)').matches && this.activeId !== trigger.dataset.welluviTrigger) {
        event.preventDefault();
        this.open(trigger.dataset.welluviTrigger);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.activeId) return;
      const trigger = this.root.querySelector(`[data-welluvi-trigger="${this.activeId}"]`);
      this.close();
      trigger?.focus();
    });

    document.addEventListener('pointerdown', (event) => {
      if (this.activeId && !this.root.contains(event.target)) this.close();
    });

    window.addEventListener('resize', () => this.positionPanels(), { passive: true });
    window.addEventListener('scroll', () => this.positionPanels(), { passive: true });
    this.media.addEventListener('change', () => this.syncMode());
  }

  cancelClose() {
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  queueOpen(id) {
    this.cancelClose();
    clearTimeout(this.openTimer);

    if (this.activeId === id) return;
    this.openTimer = setTimeout(() => this.open(id), OPEN_DELAY);
  }

  queueClose() {
    clearTimeout(this.openTimer);
    this.cancelClose();

    this.closeTimer = setTimeout(() => {
      if (this.pointerInside || this.root?.matches(':focus-within')) return;
      this.close();
    }, CLOSE_DELAY);
  }

  open(id) {
    if (!this.media.matches || !this.root) return;

    clearTimeout(this.openTimer);
    this.cancelClose();
    this.activeId = id;

    this.root.querySelectorAll('[data-welluvi-trigger][aria-controls]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(trigger.dataset.welluviTrigger === id));
    });

    this.root.querySelectorAll('[data-welluvi-panel]').forEach((panel) => {
      const active = panel.dataset.welluviPanel === id;
      panel.classList.toggle('is-open', active);
      panel.setAttribute('aria-hidden', String(!active));
    });

    document.documentElement.classList.add('welluvi-nav-open');
    this.positionPanels();
  }

  close() {
    if (!this.root) return;

    clearTimeout(this.openTimer);
    this.cancelClose();
    this.activeId = null;

    this.root.querySelectorAll('[data-welluvi-trigger][aria-controls]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    this.root.querySelectorAll('[data-welluvi-panel]').forEach((panel) => {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
    });

    document.documentElement.classList.remove('welluvi-nav-open');
  }

  positionPanels() {
    if (!this.root || !this.media.matches) return;

    /*
      Anchor the dropdown to the actual navigation row, not to Horizon's full
      header wrapper. The wrapper can include additional row height and creates
      a dead pointer gap between the top-level link and the fixed panel.
    */
    const anchor = this.navBar instanceof HTMLElement ? this.navBar : this.root;
    const bottom = Math.max(0, Math.round(anchor.getBoundingClientRect().bottom) - PANEL_OVERLAP);
    this.root.style.setProperty('--welluvi-nav-top', `${bottom}px`);
  }

  syncMode() {
    if (!this.root) return;
    const desktop = this.media.matches;
    this.root.hidden = !desktop;
    this.headerComponent.classList.toggle('welluvi-nav-desktop', desktop);
    if (!desktop) this.close();
  }
}

async function initializeWelluviNavigation() {
  if (window.welluviNavigation) return;

  /* Let Horizon register and finish the first custom-element upgrade before replacing its desktop host. */
  if (window.customElements?.whenDefined) {
    await customElements.whenDefined('header-menu').catch(() => {});
  }

  window.welluviNavigation = new WelluviNavigation();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeWelluviNavigation, { once: true });
} else {
  initializeWelluviNavigation();
}

document.addEventListener('shopify:section:load', () => {
  window.welluviNavigation = null;
  initializeWelluviNavigation();
});
